/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React from "react"
import { FloatingBox } from "../Components/Misc/FloatingBox"
import { brandColors, colors } from "../Components/EOSThemeProvider"
import { ConditionalWrapper, Grid, Icon, IconButton, List } from "@cd2/components"
import { icons } from "../constants/icons"
import { IComponentProp } from "@cd2/tools"
import { Link } from "react-router-dom"

interface IBannerProps {
  message: React.ReactNode
  to?: string
  openToInNewTab?: boolean
  compact?: boolean
  alignItems?: IComponentProp<typeof Grid, "alignItems">
  heading?: React.ReactNode
  actions?: React.ReactNode

  onDismiss?(): void
}

type IType = "info" | "warning" | "error"

interface IAlertBannersProps {
  banners: ({ type: IType } & IBannerProps)[]
}

export function AlertBanners({ banners }: IAlertBannersProps) {
  if (banners.length === 0) {
    return null
  }

  return (
    <List size={6}>
      {banners.map((banner, i) => {
        if (banner.type === "info") {
          return <InfoBanner key={i} {...banner} />
        } else if (banner.type === "warning") {
          return <WarningBanner {...banner} />
        } else if (banner.type === "error") {
          return <ErrorBanner key={i} {...banner} />
        }
      })}
    </List>
  )
}

export function ErrorBanner(props: IBannerProps) {
  return <BannerTemplate color={colors.errorNotice} icon={icons.alert} {...props} />
}

export function WarningBanner(props: IBannerProps) {
  return <BannerTemplate color={brandColors.orangeLightBackground} icon={icons.alert} {...props} />
}

export function InfoBanner(props: IBannerProps) {
  return <BannerTemplate color={colors.infoNotice} icon={icons.info} {...props} />
}

interface IBannerTemplateProps extends IBannerProps {
  icon: IComponentProp<typeof Icon, "icon">
  color: string
}

function BannerTemplate({
  heading,
  actions,
  message,
  to,
  icon,
  color,
  onDismiss,
  compact,
  alignItems,
  openToInNewTab,
}: IBannerTemplateProps) {
  return (
    <ConditionalWrapper
      condition={!!to}
      Wrapper={({ children }) => (
        <Link
          to={to!}
          css={css`
            color: #000;
          `}
          target={openToInNewTab ? "_blank" : undefined}
        >
          {children}
        </Link>
      )}
    >
      <FloatingBox
        color={color}
        css={
          compact
            ? css`
                ${FloatingBox.vars.childPadding}: 12px;
                font-size: 13px;
              `
            : undefined
        }
      >
        <Grid columns={onDismiss ? [32, { fr: 1 }, 32] : [32, { fr: 1 }]} alignItems={alignItems}>
          <Icon icon={icon} />

          <div>
            {heading && (
              <div
                css={css`
                  font-size: 16px;
                  font-weight: 600;
                  margin-bottom: 8px;
                `}
              >
                {heading}
              </div>
            )}
            <div>{message}</div>
            {actions && (
              <div
                css={css`
                  margin-top: 16px;
                `}
              >
                {actions}
              </div>
            )}
          </div>
          {onDismiss && (
            <div
              css={css`
                margin: -11px -11px -11px 0;
              `}
            >
              <IconButton
                icon={icons.cross}
                onClick={
                  ((e: any) => {
                    e.preventDefault()
                    onDismiss()
                  }) as any
                }
                buttonStyle="text"
                square
              />
            </div>
          )}
        </Grid>
      </FloatingBox>
    </ConditionalWrapper>
  )
}
